<script lang="ts">
	import Section from "#lib/web-ui/primitives/section.svelte";
	import CheckIcon from "@lucide/svelte/icons/check";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { goto } from "$app/navigation";
	import { getTemplateContext } from "../template-context.svelte";

	const templateContext = getTemplateContext();

	const sellOptions = [
		{
			id: "cash-offer",
			title: "Quick Cash Offer",
			description:
				"Sell your property as-is with no repairs, cleaning, or showings required. Receive a fair cash offer within 24 hours.",
			isRecommended: false,
			benefits: [
				"Close as quickly as 1 week",
				"No repairs or cleaning needed",
				"No fees or commissions",
				"Skip the hassle of showings"
			]
		},
		{
			id: "equity-protection",
			title: "Equity Protection Program",
			description:
				"Maximize your property's value with our expert marketing and sales support, while keeping more of your equity.",
			isRecommended: true,
			benefits: [
				"Higher potential return on your property",
				"Professional marketing and listing services",
				"No upfront costs or out-of-pocket expenses",
				"Expert guidance throughout the process"
			]
		}
	];

	let { optionOneHref, optionTwoHref }: { optionOneHref?: string; optionTwoHref?: string } =
		$props();
</script>

<Section class="relative isolate">
	<div
		class="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl"
		aria-hidden="true"
	>
		<div
			class="from-primary to-primary/80 mx-auto aspect-1155/678 w-288.75 bg-linear-to-tr opacity-30"
			style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
		></div>
	</div>
	<div class="mx-auto max-w-4xl text-center">
		<h2 class="text-primary text-lg font-bold">Options</h2>
		<p
			class="text-foreground mt-2 text-5xl font-semibold tracking-tight text-balance sm:text-6xl"
		>
			Choose the solution that's right for you
		</p>
	</div>
	<p
		class="text-muted-foreground mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty sm:text-xl/8"
	>
		We understand every seller has unique needs. Select the option that best fits your situation
		and goals.
	</p>
	<div
		class="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-2"
	>
		<div
			class="rounded-3xl rounded-t-3xl bg-white/60 p-8 ring-1 ring-gray-900/10 sm:mx-8 sm:rounded-b-none sm:p-10 lg:mx-0 lg:rounded-tr-none lg:rounded-bl-3xl"
		>
			<h3 class="text-primary text-lg font-semibold">
				{templateContext.siteName}
			</h3>
			<p class="mt-4 flex items-baseline gap-x-2">
				<span class="text-foreground text-5xl font-semibold tracking-tight"
					>Quick Cash Offer</span
				>
			</p>
			<p class="text-muted-foreground mt-6 text-base/7">
				{sellOptions[0].description}
			</p>
			<ul role="list" class="text-muted-foreground mt-8 space-y-3 text-sm/6 sm:mt-10">
				{#each sellOptions[0].benefits as benefit (benefit)}
					<li class="flex gap-x-3">
						<CheckIcon class="text-primary h-6 w-5" />
						{benefit}
					</li>
				{/each}
			</ul>
			{#if optionOneHref}
				<Button
					class="bg-primary hover:bg-primary/80 mt-8 h-14 w-full rounded-lg text-lg font-semibold"
					onclick={() => {
						goto(optionOneHref);
					}}>Explore Options</Button
				>
			{/if}
		</div>
		<div
			class="relative rounded-3xl bg-gray-900 p-8 shadow-2xl ring-1 ring-gray-900/10 sm:p-10"
		>
			<h3 class="text-primary text-lg font-semibold">
				{templateContext.siteName}
			</h3>
			<p class="mt-4 flex items-baseline gap-x-2">
				<span class="text-5xl font-semibold tracking-tight text-white"
					>Equity Protection Program</span
				>
			</p>
			<p class="text-secondary mt-6 text-base/7">
				{sellOptions[1].description}
			</p>
			<ul role="list" class="text-secondary mt-8 space-y-3 text-sm/6 sm:mt-10">
				{#each sellOptions[1].benefits as benefit (benefit)}
					<li class="flex gap-x-3">
						<CheckIcon class="text-primary h-6 w-5" />
						{benefit}
					</li>
				{/each}
			</ul>
			{#if optionTwoHref}
				<Button
					class="bg-primary hover:bg-primary/80 mt-8 h-14 w-full rounded-lg text-lg font-semibold"
					onclick={() => {
						goto(optionTwoHref);
					}}>Explore Options</Button
				>
			{/if}
		</div>
	</div>
</Section>
